import React, {useState, useEffect, useRef} from 'react'
import { columnNameAsString } from '../../XtractTableUtils.jsx'
import InputGroupCheckButton from 'GeneralUI/inputGroupCheckButton/InputGroupCheckButton.jsx'
import { autoCompleteOff } from 'Helper/autoCompleteOff.js'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

export const ShowFiltersCheckbox = ({visible, showFilters, setShowFilters, setColumnFilters, activeFilterCount = 0}) => {
  const toggle = () => {
    setShowFilters(!showFilters)
  }

  const hasActiveFilters = activeFilterCount > 0
  const tooltip = showFilters
    ? 'Turn off filters'
    : hasActiveFilters
      ? `Filter rows (${activeFilterCount} active filter${activeFilterCount === 1 ? '' : 's'} hidden)`
      : 'Filter rows'

  return visible ? (
    <div
      className={'filter-checkbox'}
      data-toggle={'tooltip'}
      data-title={tooltip}
    >
      <InputGroupCheckButton
        text={
          <div className={'filter-checkbox-icon'}>
            <FontAwesomeIcon
              icon={['fas', 'filter']}
              size="sm"
              fixedWidth
              onClick={toggle}
            />
            {hasActiveFilters ? (
              <span className={'filter-active-badge'} aria-label={`${activeFilterCount} active filters`}>
                {activeFilterCount}
              </span>
            ) : null}
          </div>
        }
        className={'control-button'}
        ariaLabel={'show filters'}
        checked={showFilters}
        onChange={toggle}
      />
    </div>
  ) : null
}

// A debounced input react component
const DebouncedInput = ({
  initialValue,
  onChange,
  onImmediateChange,
  debounce = 500,
  ...props
}) => {
  const [inputValue, setInputValue] = useState(initialValue || '')
  const pendingRef = useRef(false)

  useEffect(() => {
    const timer = setTimeout(() => {
      onChange(inputValue)
      if (pendingRef.current) {
        pendingRef.current = false
        onImmediateChange?.(false)
      }
      clearTimeout(timer)
    }, debounce)

    return () => {
      clearTimeout(timer)
    }
  }, [debounce, inputValue, onChange, onImmediateChange])

  useEffect(() => {
    setInputValue(initialValue)
  }, [initialValue])

  // if this input is still pending when it unmounts (e.g., filter row hidden),
  // release its contribution to the pending counter so the overlay can clear
  useEffect(() => {
    return () => {
      if (pendingRef.current) {
        pendingRef.current = false
        onImmediateChange?.(false)
      }
    }
  }, [onImmediateChange])

  const handleChange = (event) => {
    if (!pendingRef.current) {
      pendingRef.current = true
      onImmediateChange?.(true)
    }
    setInputValue(event.target.value)
  }

  return (
    <input
      {...props}
      value={inputValue}
      onChange={handleChange}
    />
  )
}

const defaultFilter = (column, manual, onImmediateChange) => {
  // supply our own default filter element that includes an aria-label and disables autocomplete
  const header = columnNameAsString(column.columnDef)
  const columnFilterValue = column.getFilterValue()

  return (
    <DebouncedInput
      debounce={manual ? 1000 : 500}
      aria-label={'filter for ' + header}
      type="text"
      {...autoCompleteOff}
      style={{
        width: '100%'
      }}
      initialValue={columnFilterValue || ''}
      onChange={column.setFilterValue}
      onImmediateChange={onImmediateChange}
      placeholder={column.Placeholder || 'filter...'}
    />
  )
}

export const FiltersRow = ({headerGroup, manual, table, onImmediateFilterChange}) => {
  return (
    <tr key={headerGroup.id + '-filters'}>
      {headerGroup.headers
        .filter(header => header.column.columnDef.showable !== false)
        .map(header => {
          return (
            <th
              key={header.id + '-filters'}
              colSpan={header.colSpan}
            >
              {header.isPlaceholder
                ? null
                : (
                  <div key={header.id + '-filters'}>
                    {
                      header.column.getCanFilter()
                        ? (
                          <div>
                            {header.column.columnDef.Filter
                              ? header.column.columnDef.Filter({ column: header.column, table, onImmediateChange: onImmediateFilterChange })
                              : defaultFilter(header.column, manual, onImmediateFilterChange)
                            }
                          </div>
                          )
                        : null
                    }
                  </div>
                  )}
            </th>
          )
        })}
    </tr>
  )
}